import { useAuth0 } from "@auth0/auth0-react";
import {
  Auth0AccountRole,
  OrganizationNameResponse,
  OrganizationsService,
} from "client/openapi";
import AuthenticationButton from "components/AuthenticationButton/AuthenticationButton";
import MessagesBell from "components/MessagesBell";
import PeopleSearch from "components/PeopleSearch";
import logoGreen from "images/logo-green.png";
import { Fragment, useContext, useEffect, useState, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./index.css";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import Notifications from "util/notifications";
import { useOrganizationRenamed } from "util/organizationRenamed";

type NavbarLink = {
  name: string;
  link: string;
  roles: string[];
};

export default function DefaultNavbar({
  inApp = false,
}: {
  /** On the signed-in app's pages (AppRoutes says which), not a public one. */
  inApp?: boolean;
}) {
  const { isLoading, isAuthenticated, user } = useAuth0();

  const location = useLocation();

  const { currently_selected_role, currently_selected_organization } =
    useContext(OrgRolesAndAccountContext);

  const [org, setOrg] = useState<OrganizationNameResponse>();
  const [navbarOpen, setNavbarOpen] = useState<boolean>(false);
  useOrganizationRenamed(({ id, name }) =>
    setOrg((org) => (org?.id === id ? { ...org, name } : org))
  );

  // The people search and the bell: signed-in admins with an organization,
  // inside the app. The role alone isn't enough: it is remembered after
  // signing out.
  const showAdminTools =
    isAuthenticated &&
    inApp &&
    !!currently_selected_organization &&
    (currently_selected_role === Auth0AccountRole.ORG_ADMIN ||
      currently_selected_role === Auth0AccountRole.TADPOLE_ADMIN);

  const getOrg = useCallback(async () => {
    OrganizationsService.getOrganization({
      orgId: currently_selected_organization as number,
    })
      .then((o) => setOrg(o))
      .catch(() => {
        Notifications.error("Could not get organization");
      });
  }, [currently_selected_organization]);

  const publicLinks: NavbarLink[] = [
    {
      name: "Features",
      link: "/#features",
      roles: [],
    },
    {
      name: "Contact Us",
      link: "/contact",
      roles: [],
    },
  ];

  // Dashboard is the first item of the profile dropdown.
  const primaryLinks: NavbarLink[] = [
    //  *******
    //  Commenting out to to temporarily remove fom webpage
    //  uncomment when ready to be developed
    //  *******
    // {
    //   name: "Edit Webpage",
    //   link: "/tutors/profile",
    //   roles: [Auth0AccountRole.ORG_TUTOR],
    // },
  ];

  useEffect(() => {
    if (
      !isLoading &&
      isAuthenticated &&
      user &&
      currently_selected_organization &&
      (!org || org?.id !== currently_selected_organization)
    ) {
      getOrg();
    }
  }, [
    currently_selected_organization,
    getOrg,
    isAuthenticated,
    isLoading,
    org,
    user,
  ]);

  useEffect(() => {
    setNavbarOpen(false);
  }, [location]);

  return (
    <nav className="nav">
      <div className="container-fluid px-4">
        <div className="nav__body">
          <Link to={isAuthenticated ? "/dashboard" : "/"} className="nav__logo">
            <img src={logoGreen} alt="Tadpole" />
            <span className="nav--org-name" title={org?.name}>
              {org
                ? org.name
                : "Trusted by Businesses, Nonprofits, and Schools"}
            </span>
          </Link>

          {showAdminTools && <MessagesBell className="messages-bell--narrow" />}

          <div
            className={`nav__burger ${navbarOpen && "active"}`}
            onClick={() => setNavbarOpen(!navbarOpen)}
          >
            <span></span>
          </div>

          <div className={`nav__menu ${navbarOpen && "active"}`}>
            <ul className="nav__list">
              {currently_selected_role === null
                ? // The home page and Contact are static pages outside the
                  // app (public/landing), so these links load them in full.
                  publicLinks.map((l, i) => (
                    <li key={i}>
                      <a href={l.link} className="nav__link">
                        {l.name}
                      </a>
                    </li>
                  ))
                : null}

              {primaryLinks.map((l, i) => (
                <Fragment key={i}>
                  {currently_selected_role &&
                    l.roles.includes(currently_selected_role) && (
                      <li>
                        <NavLink
                          to={l.link}
                          className={({ isActive }) =>
                            `nav__link ${isActive ? "active" : ""}`
                          }
                        >
                          {l.name}
                        </NavLink>
                      </li>
                    )}
                </Fragment>
              ))}

              {showAdminTools && (
                <>
                  <PeopleSearch />
                  <li className="messages-bell--wide">
                    <MessagesBell />
                  </li>
                </>
              )}
              <li>
                <AuthenticationButton />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
}
